// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { DivWrapper } from "@flip/XDL/constants";
import systemColors from "@flip/XDL/PreStyle/colors";
import { color, getAllColorsAsDropdownOptions, xScreen } from "@flip/XDL/Utilities";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    border = "border",
    borderValue = "borderValue",
    borderColor = "borderColor",
    padding = "padding",
    paddingSize = "paddingSize",
    breakLine = "breakLine",
    margin = "margin",
    marginSize = "marginSize",
    fullWidth = "fullWidth",
    bgColor = "bgColor",
    customBgColor = "customBgColor",
    alphaColor = "alphaColor",
    fullHeight = "fullHeight",
    // xng-change: Add this customPrint to enable the column to be full width on print
    customPrint = "customPrint",
    fullWidthMobile = "fullWidthMobile",
    hideDesktop = "hideDesktop",
    hideForMobileAndTablet = "hideForMobileAndTablet",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.border]: boolean;
        [fields.borderValue]: number;
        [fields.borderColor]: string;
        [fields.padding]: boolean;
        [fields.paddingSize]: string;
        [fields.breakLine]: boolean;
        [fields.margin]: boolean;
        [fields.marginSize]: string;
        [fields.fullWidth]: boolean;
        [fields.bgColor]: string;
        [fields.customBgColor]: string;
        [fields.alphaColor]: string;
        [fields.fullHeight]: boolean;
        // xng-change: Add this customPrint to enable the column to be full width on print
        [fields.customPrint]: boolean;
        [fields.fullWidthMobile]: boolean;
        [fields.hideDesktop]: boolean;
        [fields.hideForMobileAndTablet]: boolean;
    };
}

const breakLineStyle = (gap: number) => {
    return css`
        position: relative;
        ${xScreen("lte", "sm")} {
            margin-bottom: 20px !important;
        }

        &:not(:last-child) {
            position: relative;
            &:after {
                content: "";
                width: 2px;
                background: ${color("common.border", 0.6)};
                height: calc(100% - ${gap}px);
                position: absolute;
                right: 0;
                ${xScreen("lte", "sm")} {
                    background: ${color("common.border", 0.6)};
                    width: 100%;
                    height: 2px;
                    position: absolute;
                    bottom: -10px;

                    left: 50%;
                    transform: translateX(-50%);
                }
            }
        }
    `;
};

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

// <xng-change force the zone inside the grid item to fill its parent height>
const wrapperCss = css`
    & > div {
        height: 100%;
    }
`;
// <xng-change>

// xng-change: add the custom print style
const printFullWidthCss = css`
    @media print {
        flex-basis: 100%;
        max-width: 100%;
        width: 100%;
    }
`;
const fullWidthCss = css`
    && {
        flex-basis: 100%;
        max-width: 100%;
        width: 100%;
    }
`;

// XNG-Changes: FLIP-1094
const hideOnMobileAndTablet = css`
    display: block;
    ${xScreen("lte", "md")} {
        display: none;
    }
`;
const hideOnDesktop = css`
    display: block;
    ${xScreen("gt", "md")} {
        display: none;
    }
`;

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            border,
            borderValue,
            borderColor,
            padding,
            paddingSize,
            breakLine,
            margin,
            marginSize,
            fullWidth,
            bgColor,
            customBgColor,
            alphaColor,
            customPrint,
            fullHeight,
            fullWidthMobile,
            // XNG-Changes
            hideDesktop,
            hideForMobileAndTablet,
        },
        id,
    } = props;
    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                align={align}
                css={css`
                    ${breakLine ? breakLineStyle(gap || 0) : align === "top" ? wrapperCss : undefined}
                    ${customPrint ? printFullWidthCss : undefined}
                    ${fullWidthMobile ? fullWidthCss : undefined}
                    ${fullHeight &&
                    css`
                        ${DivWrapper} > div {
                            height: 100%;
                        }
                    `}
                `}
            >
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    const backgroundColor = bgColor === "customColor" ? customBgColor : bgColor;
    // margin && `margin: {marginSize};`
    // XNG-Changes: added hide on desktop/mobile
    return (
        <>
            <StyledWrapper
                css={css`
                    width: 100%;
                    background-color: ${color(backgroundColor, parseFloat(alphaColor))};
                    ${hideDesktop ? hideOnDesktop : undefined}
                    ${hideForMobileAndTablet ? hideOnMobileAndTablet : undefined}
                `}
            >
                <GridContainer
                    css={css`
                        &&&& {
                            ${fullWidth ? "max-width: 100%;" : ""}
                            ${!fullWidth ? "margin: auto;" : ""}
                            ${border && `border: ${borderValue};`}
                            ${border && `border-radius: 4px;`}
                            ${border && `border-color: ${borderColor};`}
                            ${padding && `padding: ${paddingSize};`}
                        }
                    `}
                    gap={fullWidth ? 0 : gap || 0}
                >
                    {columnElements}
                </GridContainer>
            </StyledWrapper>
        </>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};
const customTab = {
    displayName: "Customization",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "DropDownField",
                options: [
                    ...getAllColorsAsDropdownOptions(systemColors),
                    {
                        displayName: "Custom color",
                        value: "customColor",
                    },
                ],
                defaultValue: "",
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.customBgColor,
                displayName: "Custom color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                tab: customTab,
                isVisible: (item: HasFields) => item.fields.bgColor === "customColor",
            },
            {
                name: fields.alphaColor,
                displayName: "Color - Opacity",
                editorTemplate: "TextField",
                defaultValue: "1",
                fieldType: "General",
                placeholder: "add color opacity",
                tab: customTab,
                isVisible: (item: HasFields) => item.fields.bgColor,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return isNumeric(value) && parseFloat(value) <= 1 && parseFloat(value) > 0 ? null : `Invalid Input`;
                },
            },
            {
                name: fields.border,
                displayName: "Border",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.borderValue,
                displayName: "Border",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                placeholder: "Enter: border style",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return CSS.supports("border", value) ? null : `Invalid Input`;
                },
                isVisible: item => item.fields.border,
            },
            {
                name: fields.borderColor,
                editorTemplate: "ColorPickerField",
                defaultValue: systemColors.common.border,
                fieldType: "General",
                tab: customTab,
                isVisible: item => item.fields.border,
            },
            {
                name: fields.padding,
                displayName: "Padding",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.paddingSize,
                displayName: "Padding Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                placeholder: "Enter: 5px or any padding style",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                isVisible: item => item.fields.padding,
            },
            // {
            //     name: fields.margin,
            //     displayName: "Margin",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            //     tab: customTab,
            // },
            // {
            //     name: fields.marginSize,
            //     displayName: "Margin Size",
            //     editorTemplate: "TextField",
            //     fieldType: "General",
            //     defaultValue: "0",
            //     isRequired: true,
            //     placeholder: "Enter: 5px, 5px auto, ...etc",
            //     tab: customTab,
            //     validate: (value: string | null, item: HasFields) => {
            //         if (!value) {
            //             return `A value must be provided.`;
            //         }
            //         return CSS.supports("margin", value) ? null : `Invalid Input`;
            //     },
            //     isVisible: item => item.fields.margin,
            // },
            {
                name: fields.breakLine,
                displayName: "BreakLine for children",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.fullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },

            {
                name: fields.fullHeight,
                displayName: "Show all items with the same height",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            // XNG-Changes
            {
                name: fields.fullWidthMobile,
                displayName: "Full Width On Mobile",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.customPrint,
                displayName: "Make the columns full width on print mode",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            // XNG-Changes: FLIP-1094
            {
                name: fields.hideDesktop,
                displayName: "Hide on Desktop",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.hideForMobileAndTablet,
                displayName: "Hide on Mobile and Tablet",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
        ],
    },
};

export default widgetModule;
